import { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  subcategory: string | null;
  manufacturer: string;
  origin: string;
  price_min: number | null;
  price_max: number | null;
  description: string | null;
  images: string[] | null;
  featured: boolean;
  brochure_url: string | null;
  created_at: string;
}

interface ProductFormData {
  name: string;
  category: string;
  subcategory: string;
  manufacturer: string;
  origin: string;
  price_min: string;
  price_max: string;
  description: string;
  featured: boolean;
}

const emptyForm: ProductFormData = {
  name: '',
  category: 'Construction Machinery',
  subcategory: '',
  manufacturer: '',
  origin: 'China',
  price_min: '',
  price_max: '',
  description: '',
  featured: false,
};

const categories = [
  'Construction Machinery',
  'Industrial Equipment',
  'Agricultural Machinery',
  'Electronics & Technology',
  'Medical Equipment',
  'Building Materials',
  'Home & Office Decor',
  'Port Terminal Machinery',
  'Heavy Duty Machines Lubricants',
  'Tyres, Tubes & Flaps',
  'Automotive Spare Parts',
  'Port & Engineering Equipment',
];

const origins = ['China', 'Germany', 'Japan', 'Turkey', 'India'];

export default function AdminProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // File upload state
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [brochureFileName, setBrochureFileName] = useState('');
  const [existingBrochureUrl, setExistingBrochureUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase.from('products').select('*').order('created_at', { ascending: false });

      if (search) query = query.ilike('name', `%${search}%`);
      if (filterCategory) query = query.eq('category', filterCategory);

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      setProducts((data || []) as Product[]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [search, filterCategory]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    setBrochureFile(null);
    setBrochureFileName('');
    setExistingBrochureUrl('');
    setUploadProgress('');
    setShowForm(true);
  };

  const openEditForm = (product: Product) => {
    setEditingId(product.id);
    setForm({
      name: product.name,
      category: product.category,
      subcategory: product.subcategory || '',
      manufacturer: product.manufacturer,
      origin: product.origin,
      price_min: product.price_min?.toString() || '',
      price_max: product.price_max?.toString() || '',
      description: product.description || '',
      featured: product.featured,
    });
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages(product.images || []);
    setBrochureFile(null);
    setBrochureFileName('');
    setExistingBrochureUrl(product.brochure_url || '');
    setUploadProgress('');
    setShowForm(true);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }
    setImageFiles((prev) => [...prev, ...newFiles]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeNewImage = (idx: number) => {
    URL.revokeObjectURL(imagePreviews[idx]);
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExistingImage = (idx: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBrochureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file for the brochure.');
      return;
    }
    setBrochureFile(file);
    setBrochureFileName(file.name);
  };

  const uploadFile = async (bucket: string, file: File, folder: string): Promise<string> => {
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${folder}/${timestamp}_${safeName}`;
    const { error: uploadErr } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
    if (uploadErr) throw uploadErr;
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    return urlData.publicUrl;
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setUploadProgress('');

    try {
      // Upload new images
      const uploadedImageUrls: string[] = [];
      if (imageFiles.length > 0) {
        setUploadProgress(`Uploading ${imageFiles.length} image(s)...`);
        for (const file of imageFiles) {
          const url = await uploadFile('product-images', file, form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
          uploadedImageUrls.push(url);
        }
      }

      // Upload brochure
      let brochureUrl = existingBrochureUrl;
      if (brochureFile) {
        setUploadProgress('Uploading brochure PDF...');
        brochureUrl = await uploadFile('product-brochures', brochureFile, 'brochures');
      } else if (!editingId && !brochureFile) {
        brochureUrl = '';
      }

      // Combine existing + new images
      const allImages = [...existingImages, ...uploadedImageUrls];

      const slug = form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const data = {
        name: form.name,
        slug: editingId ? undefined : slug,
        category: form.category,
        subcategory: form.subcategory || null,
        manufacturer: form.manufacturer,
        origin: form.origin,
        price_min: form.price_min ? parseFloat(form.price_min) : null,
        price_max: form.price_max ? parseFloat(form.price_max) : null,
        description: form.description || null,
        featured: form.featured,
        images: allImages.length > 0 ? allImages : null,
        brochure_url: brochureUrl || null,
      };

      setUploadProgress('Saving product...');

      let err;
      if (editingId) {
        const { error: updateErr } = await supabase.from('products').update(data).eq('id', editingId);
        err = updateErr;
      } else {
        const { error: insertErr } = await supabase.from('products').insert(data);
        err = insertErr;
      }
      if (err) throw err;

      setShowForm(false);
      fetchProducts();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
      setUploadProgress('');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: delErr } = await supabase.from('products').delete().eq('id', id);
      if (delErr) throw delErr;
      setDeleteConfirm(null);
      fetchProducts();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900">Products</h1>
            <p className="text-sm text-foreground-500 mt-1">Manage your product catalogue</p>
          </div>
          <button
            onClick={openNewForm}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            Add Product
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-900/40 p-4">
            <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                {editingId ? 'Edit Product' : 'Add New Product'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Product Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer">
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Subcategory</label>
                    <input type="text" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Manufacturer</label>
                    <input type="text" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Origin</label>
                    <select value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 cursor-pointer">
                      {origins.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Price Min (USD)</label>
                    <input type="number" value={form.price_min} onChange={(e) => setForm({ ...form, price_min: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Price Max (USD)</label>
                    <input type="number" value={form.price_max} onChange={(e) => setForm({ ...form, price_max: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                    className="w-full px-3 py-2 text-sm border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none" />
                </div>

                {/* Product Images */}
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-2">Product Images</label>

                  {/* Existing images (edit mode) */}
                  {existingImages.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {existingImages.map((url, idx) => (
                        <div key={`existing-${idx}`} className="relative w-20 h-16 rounded-lg overflow-hidden border border-background-200 group">
                          <img src={url} alt={`Existing ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(idx)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <i className="ri-close-line text-[10px]"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* New image previews */}
                  {imagePreviews.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {imagePreviews.map((url, idx) => (
                        <div key={`new-${idx}`} className="relative w-20 h-16 rounded-lg overflow-hidden border border-accent-300 group">
                          <img src={url} alt={`New ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeNewImage(idx)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <i className="ri-close-line text-[10px]"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-dashed border-background-300 rounded-lg text-sm text-foreground-500 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-image-add-line"></i>
                    {existingImages.length > 0 || imageFiles.length > 0 ? 'Add More Images' : 'Upload Images'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                  <p className="text-[10px] text-foreground-400 mt-1">Supports JPG, PNG, WebP. Upload multiple for a gallery.</p>
                </div>

                {/* Brochure PDF */}
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-2">Product Brochure (PDF)</label>

                  {existingBrochureUrl && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-background-100 rounded-lg">
                      <i className="ri-file-pdf-line text-red-500 text-lg"></i>
                      <span className="text-xs text-foreground-600 truncate flex-1">Current brochure</span>
                      <button
                        type="button"
                        onClick={() => { setExistingBrochureUrl(''); }}
                        className="text-xs text-red-500 hover:text-red-600 cursor-pointer whitespace-nowrap"
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  {brochureFileName && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-accent-50 border border-accent-200 rounded-lg">
                      <i className="ri-file-pdf-line text-accent-600 text-lg"></i>
                      <span className="text-xs text-foreground-700 truncate flex-1">{brochureFileName}</span>
                      <button
                        type="button"
                        onClick={() => { setBrochureFile(null); setBrochureFileName(''); }}
                        className="text-xs text-foreground-500 hover:text-red-500 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}

                  <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-dashed border-background-300 rounded-lg text-sm text-foreground-500 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-upload-2-line"></i>
                    {existingBrochureUrl || brochureFile ? 'Replace Brochure' : 'Upload Brochure PDF'}
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleBrochureSelect}
                      className="hidden"
                    />
                  </label>
                  <p className="text-[10px] text-foreground-400 mt-1">Upload a PDF brochure for customers to download.</p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-500 cursor-pointer" />
                  <span className="text-sm text-foreground-700">Featured product</span>
                </label>

                {uploadProgress && (
                  <div className="flex items-center gap-2 text-sm text-accent-600 bg-accent-50 px-3 py-2 rounded-lg">
                    <div className="w-4 h-4 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
                    {uploadProgress}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-background-200">
                <button onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving || !form.name.trim()}
                  className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-900/40 p-4">
            <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-sm p-6">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">Delete Product?</h3>
              <p className="text-sm text-foreground-500 mb-5">This action cannot be undone. The product will be permanently removed.</p>
              <div className="flex items-center justify-end gap-3">
                <button onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 whitespace-nowrap cursor-pointer">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-red-600 whitespace-nowrap cursor-pointer">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Products Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
            <button onClick={fetchProducts} className="ml-3 underline cursor-pointer">Retry</button>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100">
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs">Product</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden md:table-cell">Category</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden lg:table-cell">Manufacturer</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden lg:table-cell">Origin</th>
                    <th className="text-left py-3 px-4 font-medium text-foreground-600 text-xs hidden sm:table-cell">Media</th>
                    <th className="text-right py-3 px-4 font-medium text-foreground-600 text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-foreground-500 text-sm">
                        No products found.
                      </td>
                    </tr>
                  ) : (
                    products.map((product) => (
                      <tr key={product.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                        <td className="py-3 px-4">
                          <p className="font-medium text-foreground-900 truncate max-w-[200px]">{product.name}</p>
                          <p className="text-xs text-foreground-500 md:hidden">{product.category}</p>
                        </td>
                        <td className="py-3 px-4 text-foreground-600 hidden md:table-cell">
                          <span className="inline-flex items-center gap-1">
                            {product.featured && <i className="ri-star-fill text-accent-500 text-xs" />}
                            {product.category}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-foreground-600 hidden lg:table-cell">{product.manufacturer}</td>
                        <td className="py-3 px-4 text-foreground-600 hidden lg:table-cell">{product.origin}</td>
                        <td className="py-3 px-4 hidden sm:table-cell">
                          <div className="flex items-center gap-2 text-xs text-foreground-500">
                            {product.images && product.images.length > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                <i className="ri-image-line text-accent-500"></i>
                                {product.images.length}
                              </span>
                            ) : (
                              <span className="text-foreground-400">—</span>
                            )}
                            {product.brochure_url && (
                              <span className="inline-flex items-center gap-1">
                                <i className="ri-file-pdf-line text-red-500"></i>
                                PDF
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEditForm(product)}
                              className="p-1.5 rounded-md hover:bg-background-100 text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
                              title="Edit">
                              <i className="ri-edit-line text-base" />
                            </button>
                            <button onClick={() => setDeleteConfirm(product.id)}
                              className="p-1.5 rounded-md hover:bg-red-50 text-foreground-500 hover:text-red-600 transition-colors cursor-pointer"
                              title="Delete">
                              <i className="ri-delete-bin-line text-base" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-background-200 text-xs text-foreground-500">
              {products.length} product{products.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}